<template>
    <div class="guide-form">
        <p v-if="title" class="guide-form-title">{{ title }}</p>

        <alert v-if="alert.type" :type="alert.type" :message="alert.message"></alert>

        <form action="#" @submit.prevent="handleSubmit">
            <input v-model="fields.first_name" type="text" :placeholder="trans.form.first_name" required />
            <input v-model="fields.last_name" type="text" :placeholder="trans.form.last_name" required />
            <input v-model="fields.email" type="email" :placeholder="trans.form.email" required />

            <select v-model="fields.country" name="country" :title="trans.form.country">
                <option disabled selected value="">{{ trans.form.country }}</option>
                <option v-for="(value, key) in trans.form.listCountries" :value="key">{{ value }}</option>
            </select>

            <input v-model="fields.state" type="text" :placeholder="trans.form.state" required />
            <input v-model="fields.city" type="text" :placeholder="trans.form.city" required />

            <div class="phone-wrapper">
                <select v-model="fields.code" name="code" :title="trans.form.code" @change="checkCountryCode">
                    <option disabled selected value="">{{ trans.form.code }}</option>
                    <option v-for="(value, key) in trans.form.listPhoneCodes" :value="key">{{ value }}</option>
                </select>
                <input v-model="fields.phone" name="phone" type="text" :placeholder="trans.form.phone" @input="formatPhoneNumber" maxlength="15" />
            </div>

            <p v-if="phoneError" class="error-message">{{ phoneError }}</p>

            <input v-model="fields.url" name="url" type="hidden" />

            <div class="recaptcha-wrapper">
                <vue-recaptcha
                    ref="recaptcha"
                    :loadRecaptchaScript="true"
                    :sitekey="recaptchaSiteKey"
                    @verify="onRecaptchaVerify"
                    @expired="onRecaptchaExpired"
                />
            </div>

            <button class="button button-big button-block" type="submit">{{ trans.buttons.send }}</button>
        </form>
    </div>
</template>

<style lang="scss">
    .guide-form .guide-form-title {
        font-size: 18px;
        text-align: center;
        margin-bottom: 20px;
    }

    .guide-form .alert {
        margin: 16px 0 0;
    }

    .guide-form .error-message {
        color: red !important;
        font-size: 15px !important;
        margin-top: 10px;
        margin-bottom: 0px !important;
    }

    .guide-form .phone-wrapper {
        display: flex;
        gap: 10px;

        select {
            width: 40%;
        }

        input {
            width: 60%;
        }
    }

    .guide-form .recaptcha-wrapper > div {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 20px;
    }

    .guide-form form {
        input,
        select {
            width: 100%;
            margin-bottom: 15px;
        }

        .button {
            width: 100%;
            margin-top: 15px;
        }
    }
</style>

<script>
    import axios from 'axios'
    import Alert from './AlertForm.vue'
    import VueRecaptcha from 'vue-recaptcha'

    export default {
        props: ['title', 'trans'],

        components: { Alert, VueRecaptcha },

        data() {
            return {
                alert: {
                    type: null,
                    message: null,
                },
                fields: {
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    code: '',
                    city: '',
                    state: '',
                    country: '',
                    url: window.location.hostname + window.location.pathname
                },
                phoneError: '',
                recaptchaSiteKey: window.recaptchaSiteKey || '',
                recaptchaToken: null
            }
        },

        methods: {
            handleSubmit() {
                const { first_name, last_name, email, phone, code, city, state, country } = this.fields

                if (first_name === '' || last_name === '' || email === '' || phone === '' || code === '' || city === '' || state === '' || country === '') {
                    this.showAlert(this.trans.form.messages.required, 'error')
                    return false
                }

                if (!this.recaptchaToken) {
                    this.showAlert(this.trans.recaptcha, 'error')
                    return false
                }

                const rawPhone = phone.replace(/\D/g, '');
                const countryCode = this.trans.form.listPhoneCodes[code]
                    ? this.trans.form.listPhoneCodes[code].match(/\+\d+/)[0]
                    : '';

                if (countryCode === '+55' && rawPhone.length !== 11) {
                    this.phoneError = 'O número de telefone deve conter exatamente 11 dígitos para números brasileiros.';
                    setTimeout(() => {
                        this.phoneError = '';
                    }, 3000);
                    return false;
                } else {
                    this.phoneError = '';
                }

                const cleanPhone = phone.replace(/^(\+55)?/, '');
                const payload = {
                    first_name: this.fields.first_name,
                    last_name: this.fields.last_name,
                    email: this.fields.email,
                    phone: `${countryCode}${cleanPhone}`,
                    country: this.trans.form.listCountries[country],
                    state: this.fields.state,
                    city: this.fields.city,
                    url: this.fields.url,
                    'g-recaptcha-response': this.recaptchaToken
                }

                const url = '/api/guide'
                axios.get(url, { params: payload })
                    .then(response => {
                        this.resetFormData()
                        this.showAlert(response.data.message, 'success')
                        this.resetRecaptcha()
                        this.redirectUser()
                    })
                    .catch(error => {
                        console.error(error)
                        this.showAlert(this.trans.form.messages.error, 'error')
                        this.resetRecaptcha()
                    })
            },

            onRecaptchaVerify(token) {
                this.recaptchaToken = token;
            },

            onRecaptchaExpired() {
                this.recaptchaToken = null;
            },

            resetRecaptcha() {
                this.recaptchaToken = null;
                if (this.$refs.recaptcha) {
                    this.$refs.recaptcha.reset();
                }
            },

            resetFormData() {
                Object.keys(this.fields).forEach(key => {
                    if (key !== 'url') this.fields[key] = ''
                });
                this.resetRecaptcha();
            },

            showAlert(message = '', type = null) {
                this.alert = { type, message };
            },

            redirectUser() {
                const redirectUrl = this.trans.form.redirect || '/download-guia-carnaval-do-rio';
                setTimeout(() => {
                    window.location.href = redirectUrl;
                }, 2000);
            },

            formatPhoneNumber() {
                let phone = this.fields.phone.replace(/\D/g, '');

                if (phone.length === 0) {
                    this.fields.phone = '';
                    return;
                }

                const countryCode = this.trans.form.listPhoneCodes[this.fields.code]?.replace(/[^\d+]/g, '');
                if (countryCode && countryCode.includes("+55")) {
                    if (phone.length <= 2) {
                        phone = `(${phone}`;
                    } else if (phone.length <= 7) {
                        phone = `(${phone.slice(0, 2)}) ${phone.slice(2)}`;
                    } else {
                        phone = `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7, 11)}`;
                    }
                }

                this.fields.phone = phone;
            },

            checkCountryCode() {
                this.fields.phone = '';
            }
        }
    }
</script>
